import useTableSorting from './hooks/useTableSorting/useTableSorting';
import useFormStyling from './hooks/useFormStyling/useFormStyling';
import useDynamicTable from './hooks/useDynamicTable/useDynamicTable';
import useTableSearch from './hooks/useTableSearch/useTableSearch';
import useImageExpanding from './hooks/useImageExpanding/useImageExpanding';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getClassNames } from '../../helpers/classHelpers';
import { initializeMarkdownIt } from './helpers';
import { ErrorLabels, MarkdownItWithIncrementalDOM } from '../../models/types';

import './markdown.scss'; // Markdown styles - code blocks, general line-height etc.
import 'katex/dist/katex.min.css'; // KaTeX styles for Math rendering
import style from './MarkdownWrapper.module.scss';

const unclosedMdUrl = /\[(.*?)\]\((?!.*?\))[^)]*$/gsuim;

type Props<ErrorKey extends string> = {
    isLoading?: boolean;
    disableLinkRender?: boolean;
    markdown: Parameters<MarkdownItWithIncrementalDOM['renderToIncrementalDOM']>[0];
    errorMessageDict?: ErrorLabels<ErrorKey>,
    defaultError?: ErrorKey;
    className?: string;
    imageBaseUrl?: string;
};

const latexTempWorkaroundExp = /(?<!\n)\n([ \t]{0,}\\\[)/gm;
const latexTempWorkaroundRep = "\n\n$1";
const sourcesHeadingExp = /^sources:?$/i;

const isYouTubeUrl = (url: string) => /(?:youtube\.com|youtu\.be)/i.test(url);

const getSourceBadgeType = (sourceName: string, href?: string) => {
    // Check href first — link text may be a human-readable title, not a URL
    if (href && isYouTubeUrl(href)) return 'VIDEO';

    const extension = sourceName.match(/\.([a-z0-9]+)$/i)?.[1]?.toUpperCase();
    if (extension) return extension;

    if (isYouTubeUrl(sourceName)) return 'VIDEO';
    if (/^https?:\/\//i.test(sourceName)) return 'HTML';

    return 'DOC';
};

const getSourceBadgeClass = (sourceType: string) => {
    return `sources-section-badge-${sourceType.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
};

const enhanceMarkdownSourcesSection = (container: HTMLElement) => {
    const headings = Array.from(container.querySelectorAll('p, strong, b'));

    headings.forEach((heading) => {
        const headingText = heading.textContent?.trim();
        if (!headingText || !sourcesHeadingExp.test(headingText)) return;

        let listCandidate = heading.nextElementSibling;
        if (!listCandidate) return;

        // When sources are plain text (no markdown list), wrap each line in a synthetic list
        if (listCandidate.tagName === 'P') {
            const lines = (listCandidate.textContent?.trim() ?? '').split('\n').map(l => l.trim()).filter(Boolean);
            if (!lines.length) return;
            const syntheticList = document.createElement('ul');
            lines.forEach(line => {
                const li = document.createElement('li');
                li.textContent = line;
                syntheticList.appendChild(li);
            });
            listCandidate.replaceWith(syntheticList);
            listCandidate = syntheticList;
        }
        if (listCandidate.tagName !== 'UL' && listCandidate.tagName !== 'OL') return;

        heading.classList.add('sources-section-heading');
        listCandidate.classList.add('sources-section-list');

        Array.from(listCandidate.children).forEach((child) => {
            if (!(child instanceof HTMLLIElement)) return;

            child.classList.add('sources-section-item');

            const sourceText = child.textContent?.trim() ?? '';
            const sourceLink = child.querySelector('a');
            const sourceName = sourceLink?.textContent?.trim() || sourceText;

            // Text node before the <a> is a human-readable title supplied by the LLM
            const leadingTextNode = child.firstChild?.nodeType === Node.TEXT_NODE
                ? child.firstChild.textContent?.trim() ?? ''
                : '';

            const meta = document.createElement('span');
            meta.className = 'sources-section-meta';

            const badge = document.createElement('span');
            const sourceType = getSourceBadgeType(sourceName, sourceLink?.href);
            badge.className = `sources-section-badge ${getSourceBadgeClass(sourceType)}`;
            badge.textContent = sourceType;

            const sourceNameElement = sourceLink?.cloneNode(true) as HTMLAnchorElement | null;

            //open in a new window
            if (sourceNameElement) {
                sourceNameElement.target = '_blank';
                sourceNameElement.rel = 'noopener noreferrer';
            }

            // When the LLM prefixes a title before the URL, use it as the link label
            if (sourceNameElement && leadingTextNode) {
                sourceNameElement.textContent = leadingTextNode;
            }
            const sourceLabel = sourceNameElement ?? document.createElement('span');
            sourceLabel.classList.add('sources-section-name');
            if (!sourceNameElement) sourceLabel.textContent = sourceName;

            const count = document.createElement('span');
            count.className = 'sources-section-count';
            count.textContent = '1 reference';

            meta.appendChild(badge);
            meta.appendChild(sourceLabel);

            child.innerHTML = '';
            child.appendChild(meta);
            child.appendChild(count);
        });
    });
};

const applyLatexTempWorkaround = (markdown: string) => {
    return markdown.replace(latexTempWorkaroundExp, latexTempWorkaroundRep);
};
const MarkdownWrapper = <ErrorKey extends string = string>({ disableLinkRender, isLoading, markdown, errorMessageDict, defaultError, className, imageBaseUrl }: Props<ErrorKey>) => {
    const elm = useRef<HTMLDivElement>(null);
    const [initialRenderDone, setInitialRenderDone] = useState(false);

    const md = useMemo(() => initializeMarkdownIt({ errorMessageDict, defaultError, imageBaseUrl, disableLinkRender }), [errorMessageDict, defaultError, imageBaseUrl, disableLinkRender]);

    useEffect(() => {
        const target = elm.current;
        if (!target) return;

        let suffix = markdown.match(unclosedMdUrl) ? ")" : "";

        const m = applyLatexTempWorkaround(markdown);

        window.IncrementalDOM.patch(target, md.renderToIncrementalDOM(m + suffix));
        enhanceMarkdownSourcesSection(target);
        setInitialRenderDone(true);
    }, [markdown, md]);

    const props = useMemo(() => ({ isActive: initialRenderDone && !isLoading, dependencies: [markdown] }), [initialRenderDone, isLoading, markdown]);

    const { renderModal } = useImageExpanding(props);
    useTableSorting(props);
    useTableSearch(props);
    useFormStyling(props);
    useDynamicTable(props);

    return <div className={getClassNames([className, style['markdown-wrapper'], 'markdown-body'])}>
        <div ref={elm} />
        {renderModal}
    </div>;
};


export default MarkdownWrapper;
